if (!customElements.get('upsell-modal')) {
  customElements.define(
    'upsell-modal',
    class UpsellModal extends HTMLElement {
      constructor() {
        super();
        this.modal = this.querySelector('[data-upsell-modal]');
        this.confirmationSection = this.querySelector('.cart-confirmation-section');
        this.stickyCartButton = this.querySelector('[data-upsell-modal-close][href="/cart"]');
        this.isOpen = false;
        this.redirectOnClose = false;
        this.redirectUrl = '/cart';
        this.currentUpsellContext = null;
        this.translations = {
          addToCart: this.dataset.translationAddToCart || 'Add to cart',
          soldOut: this.dataset.translationSoldOut || 'Sold out',
          adding: this.dataset.translationAdding || 'Adding...',
          added: this.dataset.translationAdded || 'Added!',
          productAddedToCart: this.dataset.translationProductAdded || 'Product added to cart',
          productImageAlt: this.dataset.translationProductImageAlt || 'Product image',
        };
      }

      connectedCallback() {
        this.checkForUpsellProducts();
        this.setupEventListeners();
      }

      disconnectedCallback() {
        if (this.keydownHandler) {
          document.removeEventListener('keydown', this.keydownHandler);
        }
      }

      checkForUpsellProducts() {
        const deferredData = this.getDeferredUpsellData();
        if (deferredData) {
          this.showWithHandles(deferredData);
          return;
        }

        const sessionData = this.getSessionUpsellData();
        if (sessionData) {
          this.showWithHandles(sessionData);
        }
      }

      getDeferredUpsellData() {
        const deferred = sessionStorage.getItem('deferredUpsell');
        if (!deferred) return null;

        try {
          const parsedDetail = JSON.parse(deferred);
          sessionStorage.removeItem('deferredUpsell');
          return parsedDetail || {};
        } catch {
          sessionStorage.removeItem('deferredUpsell');
          return null;
        }
      }

      getSessionUpsellData() {
        const handles = sessionStorage.getItem('upsellProducts');
        const addedProduct = sessionStorage.getItem('addedProduct');

        if (!handles?.trim()) return null;

        sessionStorage.removeItem('upsellProducts');
        const productData = addedProduct ? this.parseStoredProduct(addedProduct) : null;
        sessionStorage.removeItem('addedProduct');

        return {
          handles: handles.trim(),
          addedProduct: productData,
          redirectOnClose: false,
        };
      }

      parseStoredProduct(productJson) {
        try {
          return JSON.parse(productJson);
        } catch {
          return null;
        }
      }

      setupEventListeners() {
        // Close buttons
        this.querySelectorAll('[data-upsell-modal-close]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            // If it's the sticky cart button, don't prevent default navigation
            if (btn === this.stickyCartButton) {
              return; // Allow default navigation to cart
            }
            e.preventDefault();
            this.close();
          });
        });

        // Escape key
        this.keydownHandler = (e) => {
          if (e.key === 'Escape' && this.isOpen) this.close();
        };
        document.addEventListener('keydown', this.keydownHandler);

        // Form submissions
        this.addEventListener('submit', this.handleAddToCart.bind(this));
      }

      showWithHandles({
        handles,
        addedProduct,
        variantId,
        quantity = 1,
        redirectUrl = '/cart',
        redirectOnClose = true,
        parentHandle = null,
        bundleRequestId = null,
      }) {
        this.configureModalState(redirectOnClose, redirectUrl);
        this.displayConfirmation(addedProduct);
        this.currentUpsellContext = {
          source: 'modal',
          parentHandle: parentHandle || addedProduct?.handle || null,
          triggeringVariantId: variantId || null,
          bundleRequestId: bundleRequestId,
        };

        // Products are pre-rendered in Liquid, just show the modal
        this.open();
      }

      configureModalState(redirectOnClose, redirectUrl) {
        this.redirectOnClose = Boolean(redirectOnClose);
        this.redirectUrl = redirectUrl || '/cart';
      }

      displayConfirmation(addedProduct) {
        if (addedProduct) {
          this.showCartConfirmation(addedProduct);
        } else if (this.confirmationSection) {
          this.confirmationSection.classList.add('hidden');
        }
      }

      showCartConfirmation(productData) {
        if (!productData || !this.confirmationSection) return;

        const imageWrapper = this.confirmationSection.querySelector(
          '.cart-confirmation-image-wrapper'
        );
        const image = this.confirmationSection.querySelector('.cart-confirmation-image');
        const title = this.confirmationSection.querySelector('.cart-confirmation-title');

        // Handle image - show wrapper only if image exists
        if (imageWrapper && image) {
          if (productData.image) {
            image.src = productData.image;
            image.alt = productData.title || this.translations.productImageAlt || 'Product image';
            imageWrapper.classList.remove('hidden');
          } else {
            imageWrapper.classList.add('hidden');
          }
        }

        // Handle title - format as "Successfully added [Product Title] to your cart!"
        if (title && productData.title) {
          title.textContent = `${productData.title} `;
        }

        // Always show the confirmation section
        this.confirmationSection.classList.remove('hidden');
      }

      async handleAddToCart(event) {
        if (!event.target.matches('.upsell-form')) return;

        event.preventDefault();
        const form = event.target;
        const button = form.querySelector('button');
        const variantId = form.dataset.variant;

        if (button.disabled || form.dataset.available === 'false') return;

        const originalText = button.textContent;
        this.setButtonLoading(button, true);

        try {
          const success = await this.addProductToCart(variantId, this.currentUpsellContext);
          if (success) {
            this.setButtonSuccess(button);
            // Keep modal open - no automatic navigation
          } else {
            this.resetButton(button, originalText);
          }
        } catch {
          this.resetButton(button, originalText);
        }
      }

      setButtonLoading(button, loading) {
        button.disabled = loading;
        button.textContent = loading ? this.translations.adding || 'Adding...' : button.textContent;
      }

      setButtonSuccess(button) {
        button.textContent = this.translations.added || 'Added!';
        button.classList.replace('button--red', 'button--green');
      }

      resetButton(button, originalText) {
        button.disabled = false;
        button.textContent = originalText;
      }

      async addProductToCart(variantId, context = {}) {
        const payload = {
          id: variantId,
          quantity: 1,
        };

        if (context?.source) {
          payload.properties = {
            _upsell_source: context.source,
          };

          if (context?.parentHandle) {
            payload.properties._upsell_parent_handle = context.parentHandle;
          }

          if (context?.triggeringVariantId) {
            payload.properties._upsell_parent_variant_id = context.triggeringVariantId;
          }

          if (context?.bundleRequestId) {
            payload.properties._bundle_request_id = context.bundleRequestId;
          }

          if (!payload.properties._upsell_parent_handle) {
            delete payload.properties._upsell_parent_handle;
          }
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        return response.ok;
      }

      open() {
        this.isOpen = true;
        this.modal.classList.remove('hidden');
        document.body.classList.add('modal-is-open');
        this.setAttribute('aria-hidden', 'false');
        this.modal.focus();
      }

      close() {
        this.isOpen = false;
        this.modal.classList.add('hidden');
        document.body.classList.remove('modal-is-open');
        this.setAttribute('aria-hidden', 'true');

        if (this.redirectOnClose && this.redirectUrl) {
          this.redirectToCart();
        }
        this.currentUpsellContext = null;
      }

      redirectToCart(url = this.redirectUrl) {
        if (!url) {
          url = '/cart';
        }

        window.location.href = url;
      }
    }
  );
}
